import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ENV_CONFIG } from '@ps/core-config';

import { AuthMapper } from './mappers/auth.mapper';
import { AuthPayloadMapper } from './mappers/auth.payload.mapper';

import {
  LoginDto,
  LoginPayloadDto,
  ForgotPasswordDto,
  ResetPasswordDto,
  ResetPasswordPayloadDto,
  SetPasswordPayloadDto,
  VerifyTokenDto,
} from './dtos/auth.dto';
import { User } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = `${this._env.pureSpectrum.url}`;
  private readonly _mapper = new AuthMapper();
  private readonly _payloadMapper = new AuthPayloadMapper();
  public login(data: LoginPayloadDto): Observable<User> {
    const payload = this._payloadMapper.toPayload(data);

    return this._http
      .post<LoginDto>(
        `${this._baseUrl}/user/login`,
        {},
        {
          headers: {
            username: payload.email,
            password: payload.password,
            captcha: payload?.captcha || '',
          },
        },
      )
      .pipe(map((response) => this._mapper.fromDto(response)));
  }

  public forgotPassword(
    email: string,
    captcha: string | null,
  ): Observable<string> {
    return this._http
      .get<ForgotPasswordDto>(
        `${this._baseUrl}/checkForgotPasswordMail/${encodeURIComponent(email)}`,
        { headers: { captcha: captcha ?? '' } },
      )
      .pipe(map((response) => response.msg));
  }

  public resetPassword(
    userId: number,
    data: ResetPasswordPayloadDto,
  ): Observable<string> {
    return this._http
      .put<ResetPasswordDto>(
        `${this._baseUrl}/updateuserpassword/${userId}`,
        data,
      )
      .pipe(map((response) => response.msg));
  }

  public setPassword(
    token: string,
    data: SetPasswordPayloadDto,
  ): Observable<string> {
    return this._http
      .put<ResetPasswordDto>(
        `${this._baseUrl}/updateuserresetpassword/${token}`,
        data,
      )
      .pipe(map((response) => response.msg));
  }

  public verifyToken(token: string): Observable<VerifyTokenDto> {
    return this._http.get<VerifyTokenDto>(
      `${this._baseUrl}/userDataFromResetToken/${token}`,
    );
  }

  public logout(userId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/logout/${userId}`);
  }
}
